import React, { useState, useLayoutEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Footer } from 'common/Modal';
import { SubmitError } from 'common/Forms/Utils';
import { renderToString } from 'react-dom/server';
import { CheckboxField, FileField, LabelField, SelectField, TextAreaField, TextField } from 'common/Forms';
import { composeValidators, fileSizeLimiter, required } from 'common/Forms/Validators';
import { Field, Form } from 'react-final-form';
import { ImagePreview } from '../../common/Forms';
import styles from './Form.module.scss';
import useDescribeImage from './hooks/useDescribeImage.ts';
import DescriptionConfidenceField from './DescriptionConfidenceField';
import useImageFormOptions from './hooks/useImageFormOptions';

export const FILENAME_LENGTH_SERVER_ERROR = 'file name is greater than 200 characters';
export const FILENAME_LENGTH_ERROR_MESSAGE = 'Filename must be fewer than 200 characters.';
export const DEFAULT_ERROR_MESSAGE = 'Error adding image.';

const ImageModelForm = ({
  action,
  closeModal,
  defaultImage,
  dropdownOptions,
  excludeFields,
  excludeImageFileTypes,
  fromSlideShow,
  imageModel,
  isAdmin,
  onSubmit,
  submitting,
  submitError,
}) => {
  const [validationError, setValidationError] = useState();
  const [removeSpanishImage, setRemoveSpanishImage] = useState(false);
  const { describeImage, loading } = useDescribeImage();
  const inTeacherSlideshow = fromSlideShow && !isAdmin;

  const { zoom_type: zoomTypeOptions } = useImageFormOptions(dropdownOptions);

  useLayoutEffect(() => {
    // pre-populate the input with a default image file if given
    if (defaultImage) {
      const fileInputElement = document.getElementById('image_field');
      const container = new DataTransfer();
      container.items.add(defaultImage);
      fileInputElement.files = container.files;
    }
  }, []);

  const displaySizeOptions = useMemo(() => {
    const options = [
      { label: 'small', value: 'small' },
      { label: 'medium', value: 'medium' },
      { label: 'large', value: 'large' },
      { label: 'xlarge', value: 'xlarge' },
    ];

    if (isAdmin) options.push({ label: 'original', value: 'original' });

    return options;
  }, [isAdmin]);

  const allowedImageFileTypes = () => {
    const imageFileTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'];

    return imageFileTypes.filter(fileType => !excludeImageFileTypes.includes(fileType)).join(', ');
  };

  const altTextFieldTooltip = () => (
    <span>
      See
      {' '}
      <a
        href="http://teachtci.com/tci-alt-text-best-practice"
        rel="noopener noreferrer"
        target="_blank"
      >
        this document
      </a>
      {' '}
      for best practices.
    </span>
  );

  const getInitialValues = () => {
    if (imageModel) {
      return (
        {
          alignment: imageModel.alignment,
          caption_en: imageModel.captionEn,
          caption_es: imageModel.captionEs,
          credits: imageModel.image_credits,
          decorative: imageModel.decorative,
          description_confidence: imageModel.image_descriptionConfidence,
          description_en: imageModel.image_descriptionEn || imageModel.image.descriptionEn,
          description_es: imageModel.image_descriptionEs || imageModel.image.descriptionEs,
          display_size: imageModel.displaySize,
          english_image_only: imageModel.image.englishImageOnly,
          has_text: imageModel.image.hasText,
          hide_captions: imageModel.hide_captions,
          title_en: imageModel.image_titleEn,
          title_es: imageModel.image_titleEs,
          zoom_type: imageModel.zoomType
        }
      );
    }

    const defaults = {
      display_size: isAdmin ? 'original' : 'large',
      zoom_type: zoomTypeOptions[0]?.value || 'magnifier',
    };

    // pre-populate the form data with an image file if given
    if (defaultImage) defaults.image = defaultImage;

    return defaults;
  };

  const decorativeCheckbox = form => (
    <div className={styles.decorative}>
      <CheckboxField
        label="Decorative?"
        name="decorative"
        onClick={(e) => {
          form.mutators.handleDecorativeChange(e.target.value === 'true');
        }}
      />
      <span className="ml5">No Alt Text</span>
    </div>
  );

  const hideCaptionsCheckbox = form => (
    <div className={styles.decorative}>
      <CheckboxField
        label="Hide Captions"
        name="hide_captions"
        onClick={(e) => {
          form.mutators.handleHideCaptionsChange(e.target.checked);
        }}
      />
    </div>
  );

  const isDecorative = form => form.getState().values.decorative;

  const showMetaData = isAdmin || !fromSlideShow;

  const hasTextCheckbox = form => (
    <div className={styles.decorative}>
      <CheckboxField
        label="Has Text?"
        name="has_text"
        onClick={(e) => {
          form.mutators.handleHasTextChange(e.target.value === 'true');
        }}
      />
    </div>
  );

  const englishImageOnlyCheckbox = form => (
    <div className={styles.decorative}>
      <CheckboxField
        label="English Image Only"
        name="english_image_only"
        onClick={(e) => {
          form.mutators.handleEnglishImageOnlyChange(e.target.value === 'true');
        }}
      />
    </div>
  );

  const fileTypeText = () => {
    const allowedImageTypes = allowedImageFileTypes();
    const acceptedTypes = [
      allowedImageTypes.includes('image/jpeg') && '.JPEG',
      allowedImageTypes.includes('image/jpg') && '.JPG',
      allowedImageTypes.includes('image/png') && '.PNG',
      allowedImageTypes.includes('image/gif') && action.toLowerCase().includes('gif') && '.GIF'
    ].filter(Boolean);

    if (inTeacherSlideshow) {
      return (
        `Image Requirement: ${acceptedTypes.join(' / ')} and less than 2MB`
      );
    }

    return null;
  };

  const validate = (optional = false) => {
    const under2MB = fileSizeLimiter(2, 'Image');

    if (inTeacherSlideshow) {
      return (optional ? under2MB : composeValidators(required, under2MB));
    }

    if (imageModel || defaultImage || optional) return null;

    return required;
  };

  const errorMessage = submitError || validationError || DEFAULT_ERROR_MESSAGE;

  return (
    <Form
      initialValues={getInitialValues()}
      mutators={{
        handleDecorativeChange: (args, state, utils) => {
          utils.changeValue(state, 'decorative', () => args[0]);
        },
        handleDescriptionChange: (args, state, utils) => {
          utils.changeValue(state, 'description_en', () => args[0]);
        },
        handleDescriptionConfidenceChange: (args, state, utils) => {
          utils.changeValue(state, 'description_confidence', () => args[0]);
        },
        handleEnglishImageOnlyChange: (args, state, utils) => {
          utils.changeValue(state, 'english_image_only', () => args[0]);
        },
        handleHasTextChange: (args, state, utils) => {
          utils.changeValue(state, 'has_text', () => args[0]);
        },
        handleHideCaptionsChange: (args, state, utils) => {
          utils.changeValue(state, 'hide_captions', () => args[0]);
        },
        handleSpanishImageChange: (args, state, utils) => {
          utils.changeValue(state, 'remove_spanish_image', () => args[0]);
        },
      }}
      render={({
        form, handleSubmit, invalid, pristine, values
      }) => (
        <form className="tw-flex tw-flex-col tw-justify-between tw-h-full" onSubmit={handleSubmit}>
          <div className="tw-flex tw-justify-around">
            <div className="tw-flex-auto tw-w-0 tw-pr-3">
              {imageModel && (
                <LabelField
                  content={(
                    <div className="tw-flex tw-gap-2">
                      <ImagePreview src={imageModel.image.imageUrl} />
                      {imageModel.image_fileName}
                    </div>
                  )}
                  id="current_image"
                  label="Current Image (English)"
                />
              )}

              <FileField
                useImagePreview
                accept={allowedImageFileTypes()}
                fileTypeText={fileTypeText()}
                label={`${!imageModel ? 'Image' : 'Replace Image'} (English)`}
                loading={loading}
                name="image"
                required={!imageModel}
                validate={validate(true)}
                onChangeCallback={async (newFile) => {
                  const params = new FormData();
                  params.append('image', newFile);

                  if (!isAdmin) return;

                  const response = await describeImage(params);
                  const { description, confidence, errors } = response.data;
                  if ((errors || []).length > 0) {
                    setValidationError(errors.find(e => e.includes(FILENAME_LENGTH_SERVER_ERROR)) ?
                      FILENAME_LENGTH_ERROR_MESSAGE :
                      DEFAULT_ERROR_MESSAGE);
                  }

                  form.mutators.handleDescriptionChange(description);
                  form.mutators.handleDescriptionConfidenceChange(confidence);
                }}
              />

              {englishImageOnlyCheckbox(form)}

              <div>
                <div style={{ display: `${showMetaData ? '' : 'none'}` }}>
                  <TextField
                    label="Title (English)"
                    name="title_en"
                    placeholder="Enter a title of the image in English..."
                  />
                  {!excludeFields.includes('caption_en') && (
                    <TextAreaField
                      label="Caption (English)"
                      name="caption_en"
                      placeholder="Enter a caption of the image in English..."
                    />
                  )}

                  {hideCaptionsCheckbox(form)}

                  <TextField
                    label="Credits"
                    name="credits"
                    placeholder="Enter image credits..."
                  />

                  {decorativeCheckbox(form)}

                  {['Edit Image', 'Edit Background Image'].includes(action) && hasTextCheckbox(form)}

                  <TextAreaField
                    disabled={isDecorative(form)}
                    label="Alt Text (English)"
                    name="description_en"
                    placeholder="Enter a description of the image in English..."
                    tooltipText={renderToString(altTextFieldTooltip())}
                    onChangeCallback={() => {
                      // Clear description confidence if description_en is changed manually
                      form.mutators.handleDescriptionConfidenceChange(null);
                    }}
                  />

                  <DescriptionConfidenceField
                    disabled={!!values.description_confidence || !(values.image || imageModel) || loading}
                    field={(
                      <Field
                        format={value => (value ? `${(value * 100).toFixed(1)}%` : value)}
                        name="description_confidence"
                        parse={value => (value ? parseFloat(value) : value)}
                        render={({ input }) => (
                          <div>
                            <span className="tw-mx-2">
                              <i>Confidence: {input.value || 'N/A'}</i>
                            </span>
                          </div>
                        )}
                      />
                    )}
                    handleClick={async () => {
                      let params;
                      if (values.image) {
                        params = new FormData();
                        params.append('image', values.image);
                      }
                      else {
                        params = { image_model_id: imageModel.id };
                      }

                      if (!isAdmin) return;

                      const response = await describeImage(params);
                      const { description, confidence } = response.data;

                      form.mutators.handleDescriptionChange(description);
                      form.mutators.handleDescriptionConfidenceChange(confidence);
                    }}
                  />
                </div>
                {!excludeFields.includes('display_size') && (
                  <SelectField
                    name="display_size"
                    options={displaySizeOptions}
                    placeholder="Select display size..."
                  />
                )}
                <div style={{ display: `${showMetaData ? '' : 'none'}` }}>
                  {!excludeFields.includes('zoom_type') && (
                    <SelectField
                      name="zoom_type"
                      options={zoomTypeOptions}
                      placeholder="Select zoom type..."
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="tw-flex-auto tw-w-0 tw-pl-3">
              {imageModel?.image_fileNameEs && !removeSpanishImage && (
                <div className="tw-flex tw-items-center">
                  <LabelField
                    content={(
                      <div className="tw-flex tw-gap-2">
                        <ImagePreview src={imageModel.image.imageEsUrl} />
                        {imageModel.image_fileNameEs}
                      </div>
                    )}
                    id="current_image_es"
                    label="Current Image (Spanish)"
                  />
                  <button
                    className="btn btn--sm btn--red tw-ml-6"
                    type="button"
                    onClick={() => {
                      setRemoveSpanishImage(true);
                      form.mutators.handleSpanishImageChange(true);
                    }}
                  >
                    Remove Image
                  </button>
                </div>
              )}

              <div style={{ display: 'none' }}>
                <CheckboxField
                  label="Remove Image (Spanish)"
                  name="remove_spanish_image"
                />
              </div>

              <FileField
                useImagePreview
                accept={allowedImageFileTypes()}
                fileTypeText={fileTypeText()}
                label={`${!imageModel?.image_fileNameEs ? 'Image' : 'Replace Image'} (Spanish)`}
                name="image_es"
                validate={validate(true)}
              />

              <div style={{ display: `${showMetaData ? '' : 'none'}` }}>
                <TextField
                  label="Title (Spanish)"
                  name="title_es"
                  placeholder="Enter a title of the image in Spanish..."
                />

                {!excludeFields.includes('caption_es') && (
                  <TextAreaField
                    label="Caption (Spanish)"
                    name="caption_es"
                    placeholder="Enter a caption of the image in Spanish..."
                  />
                )}
                <TextAreaField
                  disabled={isDecorative(form)}
                  label="Alt Text (Spanish)"
                  name="description_es"
                  placeholder="Enter a description of the image in Spanish..."
                />
              </div>
            </div>
          </div>

          {(submitError || validationError) &&
            <SubmitError error={`${errorMessage} Please reload the page and try again.`} />
          }

          <Footer
            primaryButtonDisabled={submitError || validationError || invalid || (pristine && !defaultImage)}
            primaryButtonText="Submit"
            secondaryButtonCallback={closeModal}
            submitting={submitting}
          />
        </form>
      )}
      onSubmit={onSubmit}
    />
  );
};

ImageModelForm.propTypes = {
  action: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  defaultImage: PropTypes.object,
  excludeFields: PropTypes.arrayOf(
    PropTypes.oneOf(['display_size', 'zoom_type', 'alignment', 'caption_en', 'caption_es'])
  ),
  excludeImageFileTypes: PropTypes.arrayOf(
    PropTypes.oneOf(['image/png', 'image/jpg', 'image/jpeg', 'image/gif'])
  ),
  fromSlideShow: PropTypes.bool,
  imageModel: PropTypes.shape({
    alignment: PropTypes.string,
    captionEn: PropTypes.string,
    captionEs: PropTypes.string,
    decorative: PropTypes.bool,
    displaySize: PropTypes.string,
    hideCaptions: PropTypes.bool,
    hide_captions: PropTypes.bool,
    id: PropTypes.number.isRequired,
    image: PropTypes.shape({
      descriptionEn: PropTypes.string,
      descriptionEs: PropTypes.string,
      englishImageOnly: PropTypes.bool,
      hasText: PropTypes.bool,
      imageEsUrl: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
    image_credits: PropTypes.string,
    image_descriptionConfidence: PropTypes.number,
    image_descriptionEn: PropTypes.string,
    image_descriptionEs: PropTypes.string,
    image_fileName: PropTypes.string.isRequired,
    image_fileNameEs: PropTypes.string,
    image_titleEn: PropTypes.string,
    image_titleEs: PropTypes.string,
    zoomType: PropTypes.string,
  }),
  isAdmin: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  submitError: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
};

ImageModelForm.defaultProps = {
  dropdownOptions: {},
  excludeFields: [],
  excludeImageFileTypes: [],
  fromSlideShow: false,
  imageModel: null,
  isAdmin: false,
};

export default ImageModelForm;
